<template>
    <div>
        <filter-ui ref="asrFilter" :apiBaseUrl="apiBaseUrl" :fields="filterFields" relatedTo="asr" :filter="initialFilter" v-on:change="filterChanged"></filter-ui>
        <ejs-grid id="asrRequestsGrid"
                  ref="asrRequestsGrid"
                  :dataSource="gridData"
                  :allowSorting="true"
                  :sortSettings="sortSettings"
                  :toolbar="toolbar"
                  :allowGrouping="false"
                  :groupSettings="groupSettings"
                  :allowResizing="true"
                  :allowPaging="true"
                  :pageSettings="pageSettings"
                  :showColumnChooser="true"
                  :allowExcelExport="true"
                  :dataStateChange="gridDataStateChanged"
                  :queryCellInfo="customiseCell"
                  :toolbarClick="toolbarClicked"
                  :rowSelected="rowSelectionChanged"
                  :rowDeselected="rowSelectionChanged"
                  :headerCellInfo="headerCellInfoEvent"
                  :enableStickyHeader="true">
            <e-columns>
                <!-- :enablePersistence="true" -->
                <e-column field="orderNumber" :headerText="$t('Entities.AsrRequest.OrderNumber')" width="120"></e-column>
                <e-column field="description" :headerText="$t('Entities.AsrRequest.Description')"></e-column>
                <e-column field="audioFileCount" :headerText="$t('Entities.AsrRequest.AudioFileCount')"></e-column>
                <e-column field="asrOptions" :template="asrSelectedOptionsColumnTemplate" :headerText="$t('Entities.AsrRequest.AsrOptions')"></e-column>
                <e-column field="asrEngine" :headerText="$t('Entities.AsrRequest.AsrEngine')"></e-column>
                <e-column field="status" :headerText="$t('Entities.AsrRequest.Status')"></e-column>
                <e-column field="processedDateTimeUTC" :headerText="$t('Entities.AsrRequest.ProcessedDateTimeUTC')"></e-column>
                <e-column field="processingErrorMessage" :headerText="$t('Entities.AsrRequest.ProcessingErrorMessage')"></e-column>
                <e-column field="createdDateTimeUTC" :headerText="$t('Entities.AsrRequest.CreatedDateTimeUTC')"></e-column>
                <e-column field="createdByUserAccount" :headerText="$t('Entities.AsrRequest.CreatedByUserAccount')" :allowSorting="false" :allowGrouping="false"></e-column>

            </e-columns>
        </ejs-grid>

        <!-- new generic asr request start -->
        <validation-observer v-slot="vo" slim>
            <b-modal size="xl"
                     centered
                     no-close-on-backdrop
                     no-close-on-esc
                     v-model="newAsrRequestData.dialogOpen"
                     @shown="resetNewAsrRequestData"
                     v-on:ok.prevent="vo.handleSubmit(newAsrRequest)"
                     v-on:cancel="resetNewAsrRequestData()">
                <template #modal-header>
                    <h5 class="modal-title">New ASR Request:</h5>
                </template>
                <template #default>
                    <b-alert v-model="newAsrRequestData.showErrors" variant="danger" dismissible>
                        <div v-for="(error, index) in newAsrRequestData.currentErrors" :key="index">
                            <p>{{ error }}</p>
                        </div>
                    </b-alert>
                    <b-form-row>
                        <b-form-group label="Description"
                                      label-for="description"
                                      class="col-12">
                            <validation-provider rules="required" v-slot="asrD" name="Description">
                                <b-form-textarea name="description"
                                                 placeholder="ASR description"
                                                 v-model="newAsrRequestData.description"
                                                 rows="3"
                                                 max-rows="6"></b-form-textarea>
                                <b-form-invalid-feedback :state="asrD.errors[0] ? false : null">{{ asrD.errors[0] }}</b-form-invalid-feedback>
                            </validation-provider>
                        </b-form-group>

                    </b-form-row>

                    <b-form-row>
                        <b-form-group label="AudioFiles"
                                      label-for="audioFiles"
                                      class="col-12">
                            <attachments v-model="newAsrRequestData.attachments"
                                         :all-attachment-classifications="[ { text: 'Audio', value: 'audio' } ]"
                                         attachment-classification-selection="hidden"
                                         attachment-description-input="optional"
                                         :can-add-attachments="true"
                                         v-on:uploadInProgress="updateDisableCreate">
                            </attachments>

                        </b-form-group>
                    </b-form-row>


                    <b-alert :show="true" variant="info">
                        <i class="fas fa-info-circle"></i> Only <b>.mp3</b> and <b>.wav</b> file types are supported for ASR.
                    </b-alert>




                    <b-form-row>
                        <b-form-group class="col-2">
                            <b-form-row>
                                <label class="col-12">Asr Engine:</label>
                                <b-form-group class="col-12">
                                    <select2 v-model="newAsrRequestData.asrEngine"
                                             placeholder="Select ASR engine"
                                             :options="allAsrEngineOptions"
                                             disable-search
                                             css-class="form-control form-control-sm">
                                    </select2>
                                </b-form-group>
                            </b-form-row>
                        </b-form-group>

                        <b-form-group class="col-6">
                            <b-form-row v-if="valueExists([newAsrRequestData.asrOptions.enableSpeakerSeparation, newAsrRequestData.asrOptions.combineTranscriptResult])">
                                <label class="col-12">Transcript Options:</label>
                                <b-form-group v-if="valueExists([newAsrRequestData.asrOptions.enableSpeakerSeparation])" class="col-6">
                                    <b-form-checkbox name="enableSpeakerSeparation"
                                                     v-model="newAsrRequestData.asrOptions.enableSpeakerSeparation"
                                                     class="mb-2"
                                                     switch>
                                        Enable Speaker Separation
                                    </b-form-checkbox>
                                </b-form-group>

                                <b-form-group v-if="valueExists([newAsrRequestData.asrOptions.combineTranscriptResult])" class="col-6">
                                    <b-form-checkbox name="combineTranscriptResult"
                                                     v-model="newAsrRequestData.asrOptions.combineTranscriptResult"
                                                     class="mb-2"
                                                     switch>
                                        Combine Transcript Result
                                    </b-form-checkbox>
                                </b-form-group>
                            </b-form-row>
                        </b-form-group>
                    </b-form-row>

                    <b-alert :show="newAsrRequestData.asrOptions.enableSpeakerSeparation && newAsrRequestData.asrEngine == 'AzureWhisper'" variant="info">
                        <i class="fas fa-info-circle"></i> Only <b>mono/single channel</b> audio files are supported for Speaker Separation.
                    </b-alert>

                    <b-form-row>
                        <b-form-group class="col-3">
                            <b-form-row v-if="valueExists([newAsrRequestData.asrOptions.dictatedPunctuationMode, newAsrRequestData.asrOptions.automaticPunctuationMode])">
                                <label class="col-12">Punctuation Mode:</label>
                                <b-form-group v-if="valueExists([newAsrRequestData.asrOptions.dictatedPunctuationMode])" class="col-6">
                                    <b-form-checkbox name="dictatedPunctuationMode"
                                                     v-model="newAsrRequestData.asrOptions.dictatedPunctuationMode"
                                                     class="mb-2"
                                                     switch disabled>
                                        Dictated
                                    </b-form-checkbox>
                                </b-form-group>
                                <b-form-group v-if="valueExists([newAsrRequestData.asrOptions.automaticPunctuationMode])" class="col-6">
                                    <b-form-checkbox name="automaticPunctuationMode"
                                                     v-model="newAsrRequestData.asrOptions.automaticPunctuationMode"
                                                     class="mb-2"
                                                     switch disabled>
                                        Automatic
                                    </b-form-checkbox>
                                </b-form-group>
                            </b-form-row>
                        </b-form-group>

                        <b-form-group v-if="valueExists([newAsrRequestData.asrOptions.profanityMode])" class="col-2">
                            <b-form-row>
                                <label class="col-12">Profanity Filter Mode:</label>
                                <b-form-group class="col-12">
                                    <select2 v-model="newAsrRequestData.asrOptions.profanityMode"
                                             placeholder="Select how to handle profanity"
                                             :options="profanityOptions"
                                             disable-search
                                             css-class="form-control form-control-sm">
                                    </select2>
                                </b-form-group>
                            </b-form-row>
                        </b-form-group>

                    </b-form-row>




                </template>
                <template #modal-footer="{ ok, cancel }">
                    <button type="button" class="btn btn-secondary" @click="cancel()" :disabled="newAsrRequestData.busy">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="ok()" :disabled="newAsrRequestData.busy || newAsrRequestData.disabled">
                        <b-spinner small v-if="newAsrRequestData.busy" label="Creating new ASR request..."></b-spinner>
                        <template v-if="!newAsrRequestData.busy">
                            Create
                        </template>
                    </button>
                </template>
            </b-modal>
            <!-- new generic asr request end -->

        </validation-observer>








        <b-modal size="xl"
                 centered
                 no-close-on-backdrop
                 no-close-on-esc
                 v-model="audioDownloadData.dialogOpen"
                 v-on:cancel="audioDownloadData.dialogOpen = false">
            <template #modal-header>
                <h5 class="modal-title">Select Audio File To Download:</h5>
            </template>
            <template #default>





                <b-table striped hover small :items="audioDownloadData.audioFiles" :fields="audioTableFields">

                    <template v-slot:cell(audioFileId)="data">
                        <a v-if="data.item.audioFileId != null && fileAvailable(data.item.audioFileAvailability)" class="mx-1 btn btn-primary btn-sm" :href="audioDownloadData.url + data.item.audioFileId">Audio</a>
                        <a v-if="data.item.resultDocumentFileId != null && fileAvailable(data.item.resultFileAvailability)" class="mx-1 btn btn-primary btn-sm" :href="audioDownloadData.url + data.item.resultDocumentFileId">Transcript (.docx)</a>

                        <div v-if="!fileAvailable(data.item.audioFileAvailability)">
                            <ejs-tooltip position="TopRight" :content="fileUnavailableTooltip(data.item.audioFileAvailability)" style="display: inline;">
                                <b-icon icon="info-circle-fill" variant="secondary" style="height: 15px; width: 15px; cursor: pointer; margin-right:-7px;"></b-icon>
                            </ejs-tooltip>
                            <span style="margin-left: 7px;">
                                Audio file not available.
                            </span>
                        </div>
                        <div v-if="!fileAvailable(data.item.resultFileAvailability)">
                            <ejs-tooltip position="TopRight" :content="fileUnavailableTooltip(data.item.resultFileAvailability)" style="display: inline;">
                                <b-icon icon="info-circle-fill" variant="secondary" style="height: 15px; width: 15px; cursor: pointer; margin-right:-7px;"></b-icon>
                            </ejs-tooltip>
                            <span style="margin-left: 7px;">
                                Result file not available.
                            </span>
                        </div>

                    </template>

                </b-table>


            </template>
            <template #modal-footer="{ cancel }">
                <b-dropdown right split @click="downloadAllFiles()">

                    <template #button-content>
                        <b-spinner small v-if="audioDownloadData.busy" label="Zipping Files..."></b-spinner>
                        <span v-if="!audioDownloadData.busy">Download All</span>
                    </template>

                    <b-dropdown-item @click="downloadAllFiles('audio')">Audio Files</b-dropdown-item>
                    <b-dropdown-item v-if="!audioDownloadData.combinedTranscript" @click="downloadAllFiles('transcript')">Transcripts</b-dropdown-item>
                </b-dropdown>
                <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>
            </template>
        </b-modal>


    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import { GridPlugin, ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport, DetailRow } from '@syncfusion/ej2-vue-grids';
    import { DataManager } from "@syncfusion/ej2-data";
    import axios from 'axios';
    import fileDownload from 'js-file-download';

    import Attachments from '../Attachments.vue';
    Vue.component('attachments', Attachments);
    Vue.use(Attachments);

    import VueCurrencyInput from 'vue-currency-input'
    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import AsrSelectedOptionsColumnTemplate from './AsrSelectedOptionsColumnTemplate';

    import { extend, localize } from 'vee-validate';
    import { required, min_value, max_value, email } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('min_value', min_value);
    extend('max_value', max_value);
    extend('email', email);
    localize('en', en);

    import FilterUI from './FilterUI';

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import GridMixins from '../../mixins/GridMixins';
    import NumbersMixin from '../../mixins/NumbersMixin';
    import Select2 from '../Select2';

    Vue.use(BootstrapVue);
    Vue.use(GridPlugin)
    Vue.use(VueCurrencyInput);
    Vue.use(NumbersMixin);
    Vue.component('select2', Select2);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);

    //this is required to make the getGroupCaption function available globally and thus within the groupCaptionTemplate
    var asrRequestGrid_vueComponent = null;
    function asrRequestGrid_getGroupCaption(field, headerText, value) {
        return asrRequestGrid_vueComponent.getGroupCaption(field, headerText, value);
    }
    window.asrRequestGrid_getGroupCaption = asrRequestGrid_getGroupCaption;

    export default {
        name: 'asrRequestsGrid',
        mixins: [ErrorsMixin, DateTimeMixin, GridMixins, NumbersMixin],
        components: {
            'filter-ui': FilterUI,
        },
        provide: {
            grid: [ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport, DetailRow]
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            allAsrStatusOptions: {
                type: Array,
                default: null
            },
            allAsrEngineOptions: {
                type: Array,
                default: null
            },
            canCreateAsrRequests: {
                type: Boolean,
                default: false
            },
        },
        data: function () {
            return {

                asrSelectedOptionsColumnTemplate: function () {
                    return { template: AsrSelectedOptionsColumnTemplate }
                },

                groupSettings: { showGroupedColumn: true, disablePageWiseAggregates: true, captionTemplate: "${asrRequestGrid_getGroupCaption(field, headerText, key)}" },
                pageSettings: { pageSize: 10, pageSizes: [5, 10, 25, 50] },

                initialized: false,
                filterFields: [
                    {
                        field: "Status",
                        label: "Process Status",
                        type: "multiselect",
                        options: this.allAsrStatusOptions,
                        placeholder: "Select Status Values"
                    },
                    {
                        field: "CreatedDate",
                        label: "Created/Requested Date",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                    {
                        field: "ProcessedDate",
                        label: "Processed Date",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                ],
                quickFilters: null,
                initialFilter: null,

                sortSettings: { columns: [{ field: "createdDateTimeUTC", direction: "Descending" }] },
                gridDataState: {
                    filter: null,
                    search: null,
                    sorted: [{ name: "reference", direction: "descending" }],
                    group: null,
                    skip: 0,
                    take: 10,
                    requiresCounts: true
                },
                gridData: { result: [], count: 0 },
                grid: null,

                //currentPayment: null,
                showErrors: false,
                currentErrors: null,

                columnFormats: {
                    'createdDateTimeUTC': {
                        type: 'datetime'
                    },
                    'processedDateTimeUTC': {
                        type: 'datetime'
                    },
                },

                newAsrRequestData: {
                    dialogOpen: false,
                    currentErrors: null,
                    showErrors: false,
                    busy: false,
                    disabled: true,
                    description: null,
                    audioFiles: null,
                    attachments: [],
                    asrEngine: null,
                    asrOptions: {},
                    customMessages: {
                        description: {
                            required: 'Description is required'
                        },
                    }
                },

                audioDownloadData: {
                    dialogOpen: false,
                    audioFiles: null,
                    requestId: null,
                    combinedTranscript: false,
                    url: null,
                    busy: false,
                },
                audioTableFields: [
                    {
                        key: 'name',
                        label: 'File Name'
                    },
                    {
                        key: 'description',
                        label: 'File Description'
                    },
                    {
                        key: 'audioFileId',
                        label: 'Download'
                    },
                ],
                profanityOptions: ["None", "Removed", "Tags", "Masked"],
            }
        },
        computed: {
            toolbar: function () {
                var toolbarItems = this.defaultEJ2DataGridToolbarItems({ grouping: false, columnchooser: true, export: true });
                if (this.canCreateAsrRequests) {
                    toolbarItems =
                        toolbarItems.concat(
                            [
                                { text: "New Request", tooltipText: "New ASR Request", prefixIcon: "fas fa-plus", id: "newasrrequest" },
                            ]
                        );
                }
                toolbarItems =
                    toolbarItems.concat(
                        [
                            { text: "File Downloads", tooltipText: "Download individual files", prefixIcon: "fas fa-download", id: "downloadasrsubmit" },
                            { text: "Complete Transcript", tooltipText: "Download complete transcript file", prefixIcon: "fas fa-download", id: "downloadasrrequest" },
                        ]
                    );

                return toolbarItems;
            }
        },
        watch: {
            "newAsrRequestData.asrEngine": function (value) {
                switch (value) {
                    case "Deepgram":
                        this.newAsrRequestData.asrOptions = {
                            enableSpeakerSeparation: false,
                            combineTranscriptResult: true,
                        };
                        break;
                    case "AzureWhisper":
                        this.newAsrRequestData.asrOptions = {
                            enableSpeakerSeparation: false,
                            combineTranscriptResult: true,
                            dictatedPunctuationMode: true,
                            automaticPunctuationMode: true,
                            profanityMode: null,
                        };
                        break;
                }
            }
        },
        methods: {
            valueExists: function (values) {
                var exists = false;
                values.forEach(e => {
                    if (e !== undefined)
                        exists = true;
                });
                return exists;
            },
            fileAvailable: function (fileStatus) {
                if (fileStatus.stringValue == "Delete" || fileStatus.stringValue == "Archive")
                    return false;
                return true;
            },
            fileUnavailableTooltip: function (fileDetails) {
                var message = "";
                if (fileDetails.deletedByDataRetentionExecution != null)
                    message = "This file has been permanently deleted as per company data retention policies on the "
                        + this.formatDateTime(this.parseDateTime(fileDetails.deletedByDataRetentionExecution.completedDateTimeUTC), "DD/MM/yy")
                        + ", by rule: "
                        + fileDetails.deletedByDataRetentionExecution.dataRetentionRule.name;

                else if (fileDetails.archivedByDataRetentionExecution != null)
                    message = "This file has been archived as per company data retention policies on the "
                        + this.formatDateTime(this.parseDateTime(fileDetails.archivedByDataRetentionExecution.completedDateTimeUTC), "DD/MM/yy")
                        + ", by rule: "
                        + fileDetails.archivedByDataRetentionExecution.dataRetentionRule.name;
                else
                    message = "Error.";

                return message;
            },
            updateDisableCreate: function (uploadInProgress) {
                this.newAsrRequestData.disabled = uploadInProgress;
            },
            resetNewAsrRequestData: function () {
                this.newAsrRequestData.currentErrors = null;
                this.newAsrRequestData.busy = false;
                this.newAsrRequestData.description = null;
                this.newAsrRequestData.attachments = [];
                this.newAsrRequestData.asrEngine = (this.allAsrEngineOptions && this.allAsrEngineOptions.length == 1) ? this.allAsrEngineOptions[0].value : null;
                this.newAsrRequestData.showErrors = false;
            },
            getGroupCaption: function (field, headerText, value) {
                if (field == "createdDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                else if (field == "processedDateTimeUTC") value = this.dateTimeDisplayString(value, 'includetime', true, false);
                return headerText + ": " + value;
            },
            customizeGroupKeys(groupedDate) {
                groupedDate.forEach(g => {
                    if (g.field && g.key && g.items) {
                        if (g.field == "createdDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        else if (g.field == "processedDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'includetime', true, false);
                        this.customizeGroupKeys(g.items);
                    }
                });
            },
            customiseCell: function (args) {
                //if ((args.column.field == 'reference') && (args.name == 'queryCellInfo') && args.data.enablePaymentLink) {
                //    args.cell.innerHTML = "<a href='" + this.paymentLinkBaseUrl + args.data.paymentId + "' target='_blank'>" + args.data.reference + "</a>";
                //}
                this.defaultEJ2DataGridQueryCellInfo(args, this.columnFormats);
            },
            filterChanged: function (filter) {
                this.gridDataState.filter = filter;
                if (this.initialized) this.grid.refresh();
            },

            gridDataStateChanged: function (args) {
                this.gridDataState.search = args.search;
                this.gridDataState.sorted = args.sorted;
                this.gridDataState.group = args.group;
                this.gridDataState.skip = args.skip;
                this.gridDataState.take = args.take;
                this.gridDataState.requiresCounts = args.requiresCounts;
                if (this.initialized) {
                    this.grid.showSpinner();
                    this.loadAsrRequests(this.gridDataState)
                        .then(result => {
                            if (result) {
                                this.gridData = result;
                            }
                            this.grid.hideSpinner();
                        });
                }
            },
            toolbarClicked: function (args) {
                if (args.item.id === 'newasrrequest') {
                    this.handleAction(args.item.id, this.grid.getSelectedRecords()[0]);
                } else if (args.item.id === 'downloadasrsubmit') {
                    this.handleAction(args.item.id, this.grid.getSelectedRecords()[0]);
                } else if (args.item.id === 'downloadasrrequest') {
                    this.handleAction(args.item.id, this.grid.getSelectedRecords()[0]);
                } else if (args.item.id === 'excelexport') {
                    //to properly export all data (and not just the entries displayed on the current page) we have to load everything first
                    //we do this by taking the current grid state (which includes the current filter and search and sort settings)
                    var gridDataState = {
                        ...this.gridDataState
                    };
                    //but reset the skip and take to 0 which will return everything and unset the requiresCounts cause we are not interseted in the totals
                    gridDataState.requiresCounts = false;
                    this.grid.showSpinner();
                    this.loadAsrRequests(gridDataState)
                        .then(result => {
                            //the result is either false if something failed during the axios request, or it will contain the list in the result property which is what we have to pass to the actual export
                            if (result) {
                                var query = this.grid.getDataModule().generateQuery(true);
                                var groupedData = new DataManager(result.result).executeLocal(query);
                                this.grid.excelExport({ dataSource: groupedData });
                            }
                            this.grid.hideSpinner();
                        });
                } else {
                    this.defaultEJ2DataGridToolbarClickHandler(args, this.grid);
                }
            },
            handleAction: function (action, selectedRow) {

                if (action === 'newasrrequest') {
                    this.resetNewAsrRequestData();
                    this.newAsrRequestData.dialogOpen = true;
                }
                if (action === 'downloadasrsubmit') {
                    //selectedRow.audioFileIds.forEach(function (audioId) {
                    //var url = this.$urlHelper.getFileDownloadUrl(audioId);
                    //    window.open(url);
                    //});

                    this.audioDownloadData.url = this.$urlHelper.getFileDownloadUrl("");
                    this.audioDownloadData.audioFiles = selectedRow.audioFileList;
                    this.audioDownloadData.requestId = selectedRow.asrRequestId;
                    this.audioDownloadData.combinedTranscript = selectedRow.asrOptions.combineTranscriptResult;
                    this.audioDownloadData.dialogOpen = true;
                }
                if (action === 'downloadasrrequest') {
                    var url = this.$urlHelper.getFileDownloadUrl(selectedRow.resultFileId);
                    window.open(url);
                }
            },
            rowSelectionChanged: function (args) {
                if (args.name == 'rowSelected') {
                    this.updateToolbarItems(this.grid.getSelectedRecords()[0])
                } else {
                    this.updateToolbarItems(null)
                }
            },
            updateToolbarItems: function (item = null) {
                this.setToolbarItemState('downloadasrrequest', (item != null) && (item.resultFileId != null));
                this.setToolbarItemState('downloadasrsubmit', (item != null) && (item.audioFileCount > 0));
            },
            setToolbarItemState: function (itemId, enabled) {
                this.grid.toolbarModule.enableItems([itemId], enabled);
            },
            downloadAllFiles: function (item = null) {
                this.audioDownloadData.busy = true;
                var url = "";
                switch (item) {
                    case 'audio':
                        url = this.$urlHelper.getApiUrl('asrrequest', 'downloadall', ['audio', this.audioDownloadData.requestId]);
                        break;
                    case 'transcript':
                        url = this.$urlHelper.getApiUrl('asrrequest', 'downloadall', ['transcript', this.audioDownloadData.requestId]);
                        break;
                    default:
                        url = this.$urlHelper.getApiUrl('asrrequest', 'downloadall', [this.audioDownloadData.requestId]);
                        break;
                }

                axios
                    .get(url, { responseType: 'blob' })
                    .then(response => {
                        var contentType = response.headers["content-type"];
                        var filename = item + "files.zip";
                        var contentDisposition = response.headers["content-disposition"];
                        if (contentDisposition) {
                            var match = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
                            if (match) {
                                filename = match[1].trim().replace(/^('|")|('|")$/gm, '');
                            }
                        }
                        fileDownload(response.data, filename, contentType);
                        this.audioDownloadData.busy = false;
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                        this.audioDownloadData.busy = false;
                    });

            },
            loadAsrRequests: function (gridDataState) {
                var url = this.$urlHelper.getApiUrl('AsrRequest', 'list');

                return axios
                    .post(url, JSON.stringify(gridDataState), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        var gridData = response.data;
                        if (gridData && gridData.result) {
                            gridData.result = gridData.result.map((asr) => {
                                if (asr.createdDateTimeUTC) asr.createdDateTimeUTC = this.parseDateTime(asr.createdDateTimeUTC, true, true);
                                return asr;
                            });
                        }
                        return gridData;
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                        return false;
                    });
            },
            newAsrRequest: function () {
                this.newAsrRequestData.busy = true;
                var url = this.$urlHelper.getApiUrl('AsrRequest', 'create');


                var data =
                {
                    audioFiles: [],
                    description: this.newAsrRequestData.description,
                    asrEngine: this.newAsrRequestData.asrEngine,
                    asrOptions: {
                        ...this.newAsrRequestData.asrOptions,
                        punctuationMode: (this.newAsrRequestData.asrOptions.dictatedPunctuationMode && this.newAsrRequestData.asrOptions.automaticPunctuationMode) ?
                            "DictatedAndAutomatic" : (this.newAsrRequestData.asrOptions.dictatedPunctuationMode) ? "Dictated" :
                                (this.newAsrRequestData.asrOptions.automaticPunctuationMode) ? "Automatic" : "None"
                    },
                };

                if (data.asrOptions.profanityMode == undefined || data.asrOptions.profanityMode == null) data.asrOptions.profanityMode = "None";

                this.newAsrRequestData.attachments.forEach(attachment => {
                    data.audioFiles.push({ audioFileId: attachment.fileId, description: attachment.descriptionString });
                })



                axios
                    .post(url, JSON.stringify(data), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(() => {
                        this.resetNewAsrRequestData();
                        this.newAsrRequestData.dialogOpen = false;

                        this.$bvToast.toast('New ASR request created successfully', {
                            title: 'ASR Request Created',
                            variant: 'success',
                            appendToast: true
                        });

                        this.grid.refresh();
                    })
                    .catch(ex => {
                        this.newAsrRequestData.currentErrors = this.handleAxiosException(ex);
                        this.newAsrRequestData.showErrors = true;
                        this.newAsrRequestData.busy = false;
                    });
            },

            capitalizeFirstLetter: function (string) {
                return string.charAt(0).toUpperCase() + string.slice(1);
            }
        },

        mounted: function () {
            asrRequestGrid_vueComponent = this;
            this.grid = this.$refs.asrRequestsGrid.ej2Instances;
            this.setupGridPersistence(this.grid);
            this.defaultEJ2DataGridCreatedHandler(this.grid);


            //this.updateToolbarItems(this.grid, null);
            //this.$refs.paymentFilter.loadQuickFilters()
            //    .then(() => {
            this.initialized = true;
            setTimeout(() => {
                this.grid.refresh();
                this.updateToolbarItems();
            }, 250);
            //});
            //    this.loadAllCourtRegistries();
            //    this.loadAllInterviewOrganisations();
        }
    }
</script>

<style>
    .e-toolbar-item .e-input-group input.e-input {
        height: 29px;
    }

    .e-pagerdropdown .e-input-group.e-control-wrapper input.e-input {
        height: 29px;
    }

    .e-rule-field {
        display: flex;
        align-items: center;
    }

    .e-template-value {
        flex-grow: 1;
    }

        .e-template-value .e-multiselect {
            max-width: none !important;
        }

        .e-template-value .e-multi-select-wrapper {
            max-width: none !important;
        }
</style>